import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect } from "react";
import { setActiveThreadId } from "@/features/chat";
import { AgentChat } from "@/features/chat/components/agent-chat";
import { AgentChatSubjectBanner } from "@/features/chat/components/agent-chat/subject-banner";
import { parseThreadIdParam } from "../parse-thread-id-param";

export const Route = createFileRoute("/chat/_layout/$threadId")({
  params: {
    parse: (raw: { threadId: string }) => {
      const threadId = parseThreadIdParam(raw.threadId);
      if (threadId === null) throw notFound();
      return { threadId };
    },
  },
  component: FullPageChatRoute,
});

/**
 * MIN-239: full-page conversation for an existing thread. Renders the same
 * pieces the gutter panel does — messages, input, subject banner — at full
 * width, with the sessions rail supplied by the enclosing layout and no
 * model/system-prompt/attachment controls. `AgentChat.Root` is controlled by
 * the URL's thread id, so the conversation shown here never depends on the
 * store's `activeThreadId`.
 */
function FullPageChatRoute() {
  const { threadId } = Route.useParams();
  const navigate = useNavigate();

  // Keep the gutter panel's conversation in sync with the URL, so navigating
  // away lands the clinician on the same thread they were just viewing.
  useEffect(() => {
    setActiveThreadId(threadId);
  }, [threadId]);

  // Thread-changing actions (new chat, delete-last) report through here since
  // this route controls `AgentChat.Root`. A real id follows the clinician to
  // that thread's URL. The null case — all threads deleted — sends them to
  // the `/chat` index, which renders the new-chat composer.
  const onThreadChange = useCallback(
    (nextThreadId: string | null) => {
      if (nextThreadId === null) {
        navigate({ to: "/chat" });
      } else {
        navigate({ to: "/chat/$threadId", params: { threadId: nextThreadId } });
      }
    },
    [navigate],
  );

  return (
    <div className="flex h-full flex-col">
      <AgentChat.Root threadId={threadId} onThreadChange={onThreadChange}>
        <AgentChat.Thread className="flex-1" />
        <div className="p-3">
          <AgentChatSubjectBanner />
        </div>
      </AgentChat.Root>
    </div>
  );
}
